/**
 * Wizard de mapping — le parcours qui distingue Capalys d'un tableur.
 *
 * L'utilisateur dépose un export machine qu'il n'a jamais configuré, et voit apparaître
 * la vue d'ensemble de ses caractéristiques. Entre les deux, quatre étapes qu'il peut
 * lire **et corriger**.
 *
 * Quatre partis pris d'interface :
 *
 * 1. **Tout est affiché d'un coup, dans l'ordre de lecture**, plutôt qu'en étapes
 *    successives. Un ingénieur qualité veut vérifier que le système a compris son
 *    fichier ; l'obliger à cliquer quatre fois ajoute de la friction sans rien apporter.
 * 2. **Chaque décision automatique est justifiée à côté d'elle.** « 92 % de valeurs
 *    numériques » se valide d'un coup d'œil ; un rôle sans raison oblige à rouvrir le
 *    fichier pour vérifier.
 * 3. **Toute décision automatique est modifiable.** C'est ce qui sépare un wizard d'un
 *    afficheur : le jour où la détection se trompe, l'utilisateur corrige lui-même.
 * 4. **Le résultat vient en premier, les détails ensuite.** Le bandeau de synthèse et la
 *    vue d'ensemble sont en haut ; la structure et les rôles se replient. Un prospect
 *    juge en cinq secondes, il doit voir des mesures, pas des réglages.
 * 5. **Analyser n'écrit rien ; déposer est un geste.** Tout ce que fait cet écran est un
 *    aperçu, y compris les corrections de mapping et la saisie des tolérances. Une seule
 *    action écrit — « Déposer dans ce projet » — et elle répond par un chiffre : tant de
 *    mesures enregistrées, ou ce contenu était déjà connu. Auparavant l'écriture partait
 *    au choix du projet dans la liste, sans bouton ni confirmation : elle réussissait
 *    aussi bien qu'aujourd'hui, mais personne ne pouvait le savoir.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  FileSpreadsheet,
  Layers,
  Table2,
  Upload,
} from "lucide-react";

import { CharacteristicsTable, buildRows } from "./CharacteristicsTable";
import { DiagnosisPanel } from "./DiagnosisPanel";
import { PreviewChart } from "./PreviewChart";
import { ProjectPicker, type ProjectChoice } from "./ProjectPicker";
import { TolerancePanel } from "./TolerancePanel";
import { WindowPicker } from "./WindowPicker";
import {
  DEFAULT_WINDOW,
  ROLE_LABELS,
  isToleranceRole,
  ROLE_ORDER,
  AnalysisExpired,
  type ToleranceInput,
  analyseFile,
  changeWindowRemote,
  type AnalysisWindow,
  type AnalysedColumn,
  type AnalyseResult,
  type MappingOverrides,
} from "./mappingApi";
import { listProjects, type Project } from "./projectsApi";
import {
  Badge,
  Card,
  CardHeader,
  Field,
  Input,
  Select,
  Skeleton,
  Td,
  Th,
  cn,
  num,
} from "./ui/primitives";

/** Couleur du rôle : les valeurs mesurées et l'horodatage sont l'ossature de la carte,
 *  le contexte sert à stratifier, le reste est écarté. */
function roleTone(role: string): string {
  if (role === "value") return "text-accent";
  if (role === "timestamp" || role === "date" || role === "time")
    return "text-good";
  if (role === "ignore") return "text-faint";
  if (role === "characteristic") return "text-warn";
  return "text-ink";
}

// ---------------------------------------------------------------------------
// Dépôt de fichier
// ---------------------------------------------------------------------------

function DropZone({
  onFile,
  busy,
  compact,
}: {
  onFile: (file: File) => void;
  busy: boolean;
  compact: boolean;
}) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const handle = useCallback(
    (files: FileList | null) => {
      const file = files?.[0];
      if (file) onFile(file);
    },
    [onFile],
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        handle(e.dataTransfer.files);
      }}
      onClick={() => input.current?.click()}
      className={cn(
        "group relative cursor-pointer rounded-[var(--radius-card)] border-2 border-dashed",
        "transition-all duration-200 ease-out",
        over
          ? "border-accent bg-accent-soft"
          : "border-line hover:border-line-strong hover:bg-surface-2",
        busy && "cursor-wait",
        compact ? "px-4 py-4" : "px-6 py-12",
      )}
    >
      <input
        ref={input}
        type="file"
        accept=".csv,.txt,.tsv,.xlsx,.xlsm,.xls"
        className="hidden"
        onChange={(e) => handle(e.target.files)}
      />

      <div
        className={cn(
          "flex items-center gap-4",
          compact ? "" : "flex-col text-center",
        )}
      >
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full transition-transform duration-200",
            over
              ? "scale-110 bg-accent text-accent-ink"
              : "bg-surface-2 text-muted",
            compact ? "h-9 w-9" : "h-14 w-14",
          )}
        >
          <Upload className={compact ? "h-4 w-4" : "h-6 w-6"} />
        </div>

        <div className={cn("min-w-0", compact ? "" : "max-w-lg")}>
          <div
            className={cn(
              "font-semibold tracking-tight",
              compact ? "text-[0.9rem]" : "text-lg",
            )}
          >
            {busy ? "Analyse en cours…" : "Déposer un export machine"}
          </div>
          <p
            className={cn(
              "text-muted",
              compact ? "text-[0.75rem]" : "mt-1.5 text-[0.85rem]",
            )}
          >
            CSV, TXT, TSV ou classeur Excel, tel que la machine le produit.
            Aucune préparation : séparateur, encodage, lignes de bandeau,
            feuille du classeur et formats de date sont détectés
            automatiquement.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bandeau de synthèse — ce qu'un prospect lit en premier
// ---------------------------------------------------------------------------

function Stat({
  value,
  label,
  tone = "ink",
}: {
  value: string;
  label: string;
  tone?: "ink" | "good" | "warn" | "bad" | "muted";
}) {
  const colour = {
    ink: "text-ink",
    good: "text-good",
    warn: "text-warn",
    bad: "text-bad",
    muted: "text-muted",
  }[tone];

  return (
    <div className="min-w-0">
      <div
        className={cn(
          "tnum text-2xl leading-none font-semibold tracking-tight",
          colour,
        )}
      >
        {value}
      </div>
      <div className="mt-1 text-[0.7rem] tracking-wide text-faint uppercase">
        {label}
      </div>
    </div>
  );
}

function Summary({ result }: { result: AnalyseResult }) {
  const unstable = result.charts.filter(
    (c) =>
      c.rules_evaluated_on && c.violations_total / c.rules_evaluated_on >= 0.05,
  ).length;

  return (
    <Card className="animate-[rise_0.35s_cubic-bezier(0.16,1,0.3,1)_both] overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted" />
        <span className="truncate text-[0.85rem] font-medium">
          {result.filename}
        </span>
        {result.structure.source_format === "excel" &&
          result.structure.sheet_name && (
            <Badge tone="neutral">
              feuille « {result.structure.sheet_name} »
            </Badge>
          )}
        <span className="ml-auto font-mono text-[0.7rem] text-faint">
          empreinte {result.fingerprint}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 px-4 py-4 sm:grid-cols-4">
        <Stat
          value={num(result.measurements_total)}
          label="mesures extraites"
        />
        <Stat
          value={num(result.characteristics_found.length)}
          label="caractéristiques"
        />
        <Stat
          value={num(unstable)}
          label="hors contrôle"
          tone={unstable > 0 ? "bad" : "good"}
        />
        <Stat
          value={
            result.rejects_total === 0 ? "aucun" : num(result.rejects_total)
          }
          label="rejets"
          tone={result.rejects_total > 0 ? "warn" : "muted"}
        />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sections repliables
// ---------------------------------------------------------------------------

function Section({
  step,
  title,
  hint,
  right,
  defaultOpen = true,
  children,
}: {
  step: number;
  title: string;
  hint?: string;
  right?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="animate-[rise_0.35s_cubic-bezier(0.16,1,0.3,1)_both] overflow-hidden">
      <div className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 items-start gap-2 text-left"
        >
          <span className="tnum mt-px inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[0.7rem] font-bold text-accent">
            {step}
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 text-[0.9rem] font-semibold tracking-tight">
              {title}
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 text-faint transition-transform",
                  !open && "-rotate-90",
                )}
              />
            </span>
            {hint && (
              <span className="mt-0.5 block text-[0.78rem] text-muted">
                {hint}
              </span>
            )}
          </span>
        </button>
        {right && <div className="shrink-0">{right}</div>}
      </div>
      {open && (
        <div className="animate-[fade_0.25s_ease-out_both]">{children}</div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Étape 1 — structure
// ---------------------------------------------------------------------------

function StructureSection({
  result,
  onChange,
}: {
  result: AnalyseResult;
  onChange: (patch: MappingOverrides) => void;
}) {
  const s = result.structure;
  const isExcel = s.source_format === "excel";

  return (
    <Section
      step={1}
      title="Structure détectée"
      hint="Modifiable si nous nous trompons"
      defaultOpen={false}
    >
      <div className="flex flex-wrap gap-x-6 gap-y-3 px-4 py-4">
        {isExcel && s.sheet_names.length > 0 && (
          <Field label="Feuille">
            <Select
              value={s.sheet_name ?? ""}
              onChange={(e) => onChange({ sheet_name: e.target.value })}
            >
              {s.sheet_names.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {!isExcel && (
          <>
            <Field label="Séparateur">
              <Select
                value={s.delimiter}
                onChange={(e) => onChange({ delimiter: e.target.value })}
              >
                <option value=";">point-virgule ;</option>
                <option value=",">virgule ,</option>
                <option value={"\t"}>tabulation</option>
                <option value="|">barre verticale |</option>
              </Select>
            </Field>
            <Field label="Encodage">
              <span className="flex h-7 items-center font-mono text-[0.8rem]">
                {s.encoding}
              </span>
            </Field>
          </>
        )}

        <Field label="Décimale">
          <Select
            value={s.decimal_separator}
            onChange={(e) =>
              onChange({ decimal_separator: e.target.value as "." | "," })
            }
          >
            <option value=",">virgule — 10,25</option>
            <option value=".">point — 10.25</option>
          </Select>
        </Field>

        <Field label="Lignes ignorées">
          <Input
            type="number"
            min={0}
            value={s.skip_rows}
            className="w-16"
            onChange={(e) =>
              onChange({ skip_rows: Math.max(0, Number(e.target.value)) })
            }
          />
        </Field>

        <Field label="Première ligne">
          <Select
            value={s.has_header ? "header" : "data"}
            onChange={(e) =>
              onChange({ has_header: e.target.value === "header" })
            }
          >
            <option value="header">en-tête</option>
            <option value="data">déjà des données</option>
          </Select>
        </Field>

        <Field label="Disposition">
          <Select
            value={s.orientation}
            onChange={(e) =>
              onChange({ orientation: e.target.value as "long" | "wide" })
            }
          >
            <option value="wide">large — une colonne par cote</option>
            <option value="long">longue — une ligne par mesure</option>
          </Select>
        </Field>
      </div>

      {s.warnings.length > 0 && (
        <div className="border-t border-line px-4 py-3">
          {s.warnings.map((w) => (
            <div key={w} className="flex gap-2 py-0.5 text-[0.8rem] text-muted">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      <details className="border-t border-line px-4 py-2">
        <summary className="cursor-pointer text-[0.78rem] text-muted hover:text-ink">
          Voir le fichier tel qu'il a été reçu
        </summary>
        <pre className="mt-2 max-h-56 overflow-auto rounded-md bg-surface-2 p-3 font-mono text-[0.72rem] leading-relaxed text-muted">
          {s.raw_preview.join("\n")}
        </pre>
      </details>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Étape 2 — rôles
// ---------------------------------------------------------------------------

function ColumnsSection({
  columns,
  uncertain,
  orientation,
  mesures,
  onChange,
}: {
  columns: AnalysedColumn[];
  uncertain: number[];
  orientation: string;
  /** Caractéristiques réellement mesurées dans ce fichier, pour rattacher les bornes. */
  mesures: string[];
  onChange: (patch: MappingOverrides) => void;
}) {
  const confident = columns.length - uncertain.length;

  return (
    <Section
      step={2}
      title="Rôle de chaque colonne"
      hint={`${confident} sur ${columns.length} identifiées avec certitude`}
      defaultOpen={false}
    >
      <div className="max-h-[28rem] overflow-auto">
        <table className="w-full border-collapse text-[0.82rem]">
          <thead className="sticky top-0 bg-surface">
            <tr>
              <Th>Colonne</Th>
              <Th>Exemples</Th>
              <Th>Rôle</Th>
              <Th>Caractéristique</Th>
              <Th>Pourquoi</Th>
            </tr>
          </thead>
          <tbody>
            {columns.map((c) => (
              <tr
                key={c.index}
                className={cn(!c.is_confident && "bg-warn-soft/40")}
              >
                <Td className="font-mono text-[0.78rem] font-medium">
                  {c.header || (
                    <em className="text-faint">colonne {c.index + 1}</em>
                  )}
                </Td>
                <Td className="font-mono text-[0.72rem] text-faint">
                  {c.samples.slice(0, 3).join("  ·  ")}
                </Td>
                <Td>
                  <Select
                    value={c.role}
                    className={cn("font-medium", roleTone(c.role))}
                    onChange={(e) =>
                      onChange({ roles: { [c.index]: e.target.value } })
                    }
                  >
                    {ROLE_ORDER.map((role) => (
                      <option key={role} value={role} className="text-ink">
                        {ROLE_LABELS[role] ?? role}
                      </option>
                    ))}
                  </Select>
                  {!c.is_confident && (
                    <div className="mt-0.5 text-[0.68rem] text-warn">
                      à confirmer
                    </div>
                  )}
                </Td>
                <Td>
                  {isToleranceRole(c.role) ? (
                    /* Une borne ne se suffit pas à elle-même : « mini de quoi ? ».
                       Sans réponse, on saurait qu'une limite existe quelque part sans
                       savoir ce qu'elle borne — donc sans pouvoir calculer le moindre
                       indice. La liste ne propose que des caractéristiques réellement
                       mesurées dans ce fichier : rattacher une tolérance à une cote
                       absente produirait un Cpk contre une spécification étrangère,
                       faux et plausible. */
                    <Select
                      value={c.characteristic_code ?? ""}
                      className="w-40 font-mono text-[0.72rem]"
                      onChange={(e) => {
                        onChange({
                          characteristic_codes: { [c.index]: e.target.value },
                        });
                      }}
                    >
                      <option value="">borne de quelle mesure ?</option>
                      {mesures.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </Select>
                  ) : c.role === "value" && orientation === "wide" ? (
                    <Input
                      defaultValue={c.characteristic_code ?? ""}
                      className="w-32 font-mono text-[0.75rem]"
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value && value !== c.characteristic_code) {
                          onChange({
                            characteristic_codes: { [c.index]: value },
                          });
                        }
                      }}
                    />
                  ) : (
                    <span className="text-faint">—</span>
                  )}
                </Td>
                <Td className="text-[0.78rem] text-muted">{c.rationale}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {uncertain.length > 0 && (
        <div className="border-t border-line px-4 py-2.5 text-[0.75rem] text-muted">
          Les lignes surlignées n'ont pas pu être qualifiées avec certitude. Ce
          sont celles — et uniquement celles — qui seront soumises à l'assistant
          IA, ou que vous désignerez vous-même.
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Étape 3 — mesures et rejets
// ---------------------------------------------------------------------------

function MeasurementsSection({ result }: { result: AnalyseResult }) {
  return (
    <Section
      step={3}
      title="Mesures interprétées"
      hint={`${num(result.measurements_total)} mesure(s)${
        result.rejects_total
          ? `, ${num(result.rejects_total)} ligne(s) écartée(s)`
          : ""
      }`}
      defaultOpen={false}
    >
      <table className="w-full border-collapse text-[0.8rem]">
        <thead>
          <tr>
            <Th>Caractéristique</Th>
            <Th className="text-right">Valeur</Th>
            <Th>Horodatage</Th>
            <Th>Contexte</Th>
          </tr>
        </thead>
        <tbody>
          {result.measurements_preview.slice(0, 8).map((m, i) => (
            <tr key={`${m.source_row}-${i}`}>
              <Td className="font-mono text-[0.76rem]">
                {m.characteristic_code}
              </Td>
              <Td className="tnum text-right font-medium">{m.value}</Td>
              <Td className="tnum text-muted">
                {new Date(m.ts).toLocaleString("fr-FR")}
              </Td>
              <Td className="text-muted">
                {Object.entries(m.context).length === 0 ? (
                  <span className="text-faint">—</span>
                ) : (
                  <span className="flex flex-wrap gap-1">
                    {Object.entries(m.context).map(([k, v]) => (
                      <Badge key={k} tone="neutral">
                        {k} : {v}
                      </Badge>
                    ))}
                  </span>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      {result.rejects_total > 0 && (
        <details className="border-t border-line px-4 py-2.5">
          <summary className="cursor-pointer text-[0.8rem] text-warn">
            {Object.entries(result.rejects_by_reason)
              .map(([reason, n]) => `${num(n)} × ${reason.replace(/_/g, " ")}`)
              .join(", ")}
          </summary>
          <table className="mt-2 w-full border-collapse text-[0.75rem]">
            <tbody>
              {result.rejects_preview.map((r) => (
                <tr key={r.row_number}>
                  <Td className="tnum whitespace-nowrap text-faint">
                    ligne {r.row_number}
                  </Td>
                  <Td>{r.detail}</Td>
                  <Td className="font-mono text-[0.7rem] text-faint">
                    {r.raw.slice(0, 60)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1.5 text-[0.72rem] text-faint">
            Les lignes écartées n'empêchent pas le reste du fichier d'être
            exploité.
          </p>
        </details>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

/**
 * Ce qu'un dépôt a produit, tel qu'on le dit à l'utilisateur.
 *
 * Conservé à part du résultat d'analyse, et non déduit de `file_id` : une analyse
 * relancée pour une autre fenêtre ne réécrit rien et renverrait `file_id` nul, ce qui
 * ferait disparaître la confirmation d'un dépôt pourtant bien enregistré. On efface donc
 * cette trace sur les seuls événements qui changent ce qui serait écrit — nouveau
 * fichier, correction de mapping, saisie de tolérance.
 */
interface Deposited {
  measurements: number;
  /** Ce contenu était déjà en base : aucune mesure n'a été dupliquée. */
  already: boolean;
  project: string;
  rejected: number;
}

function LoadingState() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

export function MappingWizard() {
  const [file, setFile] = useState<File | null>(null);
  const [overrides, setOverrides] = useState<MappingOverrides>({});
  const [result, setResult] = useState<AnalyseResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [window, setWindow] = useState<AnalysisWindow>(DEFAULT_WINDOW);
  const [natures, setNatures] = useState<Record<string, string>>({});
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [choice, setChoice] = useState<ProjectChoice>({
    projectId: null,
    projectName: null,
  });
  const [deposit, setDeposit] = useState<Deposited | null>(null);

  // La liste des projets est chargée avant tout dépôt, pas au moment où le sélecteur
  // s'affiche : elle apparaît alors déjà remplie. Une liste qui se peuple sous le
  // curseur fait cliquer à côté.
  useEffect(() => {
    listProjects()
      .then(setProjects)
      // Un échec ici n'empêche rien : la reconnaissance de format se fait côté serveur,
      // et le champ « nommer un projet » reste utilisable sans liste.
      .catch(() => setProjects([]))
      .finally(() => setLoadingProjects(false));
  }, []);

  const run = useCallback(
    (
      target: File,
      next: MappingOverrides,
      w: AnalysisWindow,
      persist = false,
      destination: ProjectChoice = { projectId: null, projectName: null },
    ) => {
      setBusy(true);
      setError(null);
      analyseFile(target, next, w, persist, {
        projectId: destination.projectId,
        projectName: destination.projectName,
        // Jamais de repli automatique depuis l'interface : la destination d'une écriture
        // est toujours celle que l'utilisateur a désignée en cliquant. Le serveur refuse
        // alors d'écrire s'il n'en reçoit aucune, ce qui rend la garantie vérifiable
        // ailleurs que dans ce fichier.
        useRecognition: false,
      })
        .then((r) => {
          setResult(r);
          setError(null);
          if (persist && r.file_id) {
            setDeposit({
              measurements: r.measurements_stored,
              already: r.already_ingested,
              project: r.project_name,
              rejected: r.rejects_total,
            });
          }
          // Le projet retenu par le serveur — désigné ou créé — devient le choix courant.
          // Sans cela, le sélecteur retomberait sur « choisir un projet… » à chaque
          // correction de mapping, et une correction de colonne pourrait faire changer le
          // fichier de projet sans qu'on le voie.
          if (r.project_id) {
            setChoice({ projectId: r.project_id, projectName: null });
            setProjects((liste) =>
              liste.some((p) => p.id === r.project_id)
                ? liste
                : [
                    {
                      id: r.project_id as string,
                      name: r.project_name,
                      kind: "upload",
                      created_at: new Date().toISOString(),
                      files_count: 1,
                      measurements_count: r.measurements_stored,
                      characteristics_count: r.characteristics_found.length,
                      last_deposit_at: new Date().toISOString(),
                    },
                    ...liste,
                  ],
            );
          } else if (r.recognition?.project_id) {
            // Format reconnu : la destination est **proposée**, pas appliquée. Elle
            // s'inscrit dans le sélecteur pour que le dépôt quotidien reste à un clic,
            // et ce clic reste indispensable. Un choix déjà exprimé n'est pas écrasé :
            // l'utilisateur qui vient de désigner un autre projet le verrait revenir en
            // arrière à la première correction de colonne.
            const propose = r.recognition.project_id;
            setChoice((courant) =>
              courant.projectId || courant.projectName
                ? courant
                : { projectId: propose, projectName: null },
            );
          }
          // La caractéristique choisie est conservée si elle existe toujours.
          //
          // Elle était réinitialisée à chaque analyse, y compris quand l'analyse était
          // relancée par une simple saisie de tolérance : on tapait une limite sur la
          // cote n° 7 et l'écran revenait sur la n° 1. Le travail était perdu à chaque
          // frappe validée, ce qui rendait la saisie multi-cotes impraticable.
          setSelected((courant) =>
            courant && r.charts.some((c) => c.characteristic_code === courant)
              ? courant
              : (r.charts[0]?.characteristic_code ?? null),
          );
        })
        .catch((e: unknown) => {
          // Un dépôt refusé ne fait pas perdre l'analyse.
          //
          // Le refus est fréquent et rattrapable — nom de projet déjà pris, droit
          // manquant, session expirée — et effacer les cartes pour l'annoncer punit
          // l'utilisateur d'une erreur qu'il corrige en trois secondes. Il devait alors
          // redéposer son fichier pour retrouver ce qu'il regardait, ce qui ressemble à
          // « le bouton ne fait rien » bien plus qu'à un refus.
          //
          // Une analyse qui échoue, elle, n'a rien laissé à l'écran : dans ce cas
          // l'effacement est juste, il n'efface rien.
          setResult((precedent) => (persist ? precedent : null));
          setError(e instanceof Error ? e.message : "erreur inconnue");
        })
        .finally(() => setBusy(false));
    },
    [],
  );

  const onFile = useCallback(
    (target: File) => {
      // Un nouveau fichier repart des corrections vides : celles du précédent
      // porteraient sur des index de colonnes qui n'ont plus le même sens.
      //
      // Le projet aussi repart de zéro : un fichier déposé après un autre vient
      // presque toujours d'une autre machine, et hériter du projet précédent
      // mélangerait deux séries sans rien signaler. C'est la reconnaissance de
      // format qui décide, et elle décide sur ce fichier-ci.
      setFile(target);
      setOverrides({});
      setWindow(DEFAULT_WINDOW);
      setNatures({});
      setChoice({ projectId: null, projectName: null });
      setDeposit(null);
      run(target, {}, DEFAULT_WINDOW, false, {
        projectId: null,
        projectName: null,
      });
    },
    [run],
  );

  /** Change la destination proposée, et rejoue l'aperçu **seulement si ça sert**.
   *
   *  Désigner un projet **existant** fait comparer le fichier au gabarit de ce projet-là,
   *  donc peut réappliquer des rôles déjà validés : l'aperçu est rejoué. Un projet qu'on
   *  vient de **nommer** n'a aucun gabarit — rejouer ne produirait rien de différent.
   *
   *  Ce n'est pas une optimisation, c'est la correction d'un défaut visible. Cliquer sur
   *  « Déposer » commence par sortir le champ de saisie du focus ; ce `blur` validait le
   *  nom, relançait un aperçu, mettait le bouton en attente — donc désactivé — **avant**
   *  que le clic ne soit délivré. Un bouton désactivé n'exécute pas son gestionnaire : le
   *  premier clic était avalé, et l'écran ne bougeait pas. Il fallait cliquer deux fois
   *  sans savoir pourquoi.
   *
   *  Rien n'est enregistré au passage dans les deux cas — c'est le bouton qui écrit, et
   *  lui seul. */
  const changeProject = useCallback(
    (next: ProjectChoice) => {
      setChoice(next);
      // La confirmation d'un dépôt nomme son projet ; elle n'a plus de sens dès qu'on
      // vise ailleurs. Déposer le même fichier dans un second projet est légitime — c'est
      // ainsi qu'on compare deux réglages — et demande donc un nouveau clic, avec son
      // propre décompte.
      setDeposit(null);
      if (!file) return;
      if (!next.projectId) return;
      run(file, overrides, window, false, next);
    },
    [file, overrides, run, window],
  );

  /** Écrit le dépôt dans le projet désigné. Le seul chemin qui écrive quoi que ce soit.
   *
   *  La destination arrive du sélecteur plutôt que de l'état : un nom tapé et validé par
   *  le clic lui-même n'aurait pas encore remonté ici, et le dépôt partirait sans projet —
   *  ce que le serveur refuserait, mais l'utilisateur ne verrait qu'un bouton sans effet. */
  const deposer = useCallback(
    (destination: ProjectChoice) => {
      if (!file) return;
      if (!destination.projectId && !destination.projectName) return;
      setChoice(destination);
      run(file, overrides, window, true, destination);
    },
    [file, overrides, run, window],
  );

  /** Changer de fenêtre recalcule limites, règles et indices sur le nouveau segment —
   *  ce n'est pas un filtre d'affichage, et le traiter comme tel produirait des chiffres
   *  faux. Mais le fichier n'est pas renvoyé : le serveur a gardé les séries, seule
   *  l'identification de l'analyse circule.
   *
   *  Si cette analyse a expiré, on repart du fichier sans rien demander à l'utilisateur :
   *  il constatera un délai, pas une erreur. */
  const changeWindow = useCallback(
    (next: AnalysisWindow) => {
      setWindow(next);
      if (!file) return;
      if (!result?.analysis_id) {
        run(file, overrides, next, false, choice);
        return;
      }

      setBusy(true);
      changeWindowRemote(result.analysis_id, next, natures)
        .then((fresh) => {
          setResult((previous) =>
            previous ? { ...previous, ...fresh } : previous,
          );
          setBusy(false);
        })
        .catch((e: unknown) => {
          if (e instanceof AnalysisExpired) {
            run(file, overrides, next, false, choice);
            return;
          }
          setError(e instanceof Error ? e.message : "erreur inconnue");
          setBusy(false);
        });
    },
    [choice, file, natures, overrides, result, run],
  );

  /** Fusionne une correction et relance l'analyse. Les dictionnaires par index sont
   *  fusionnés et non remplacés : corriger une colonne ne doit pas effacer les
   *  corrections précédentes. */
  const patch = useCallback(
    (change: MappingOverrides) => {
      if (!file) return;
      const next: MappingOverrides = {
        ...overrides,
        ...change,
        roles: { ...overrides.roles, ...change.roles },
        characteristic_codes: {
          ...overrides.characteristic_codes,
          ...change.characteristic_codes,
        },
        datetime_formats: {
          ...overrides.datetime_formats,
          ...change.datetime_formats,
        },
        tolerances: { ...overrides.tolerances, ...change.tolerances },
      };
      // Changer de feuille invalide les corrections de colonnes : ce ne sont plus les
      // mêmes colonnes. Les garder produirait des rôles absurdes sans explication.
      if (change.sheet_name && change.sheet_name !== overrides.sheet_name) {
        next.roles = {};
        next.characteristic_codes = {};
        next.datetime_formats = {};
      }
      setOverrides(next);
      // Une correction change ce qui serait écrit : la confirmation du dépôt précédent ne
      // décrit plus ce qui est à l'écran, et le bouton redevient disponible. Rejouer le
      // dépôt est sans danger — le contenu déjà ingéré est reconnu et non dupliqué —, mais
      // laisser « Déposé » affiché ferait croire que la correction est, elle aussi, en base.
      setDeposit(null);
      run(file, next, window, false, choice);
    },
    [choice, file, overrides, run, window],
  );

  /** Enregistre une borne de tolérance et recalcule les indices.
   *
   *  Passe par une nouvelle analyse et non par un calcul local : la capabilité dépend de
   *  la fenêtre et du sigma estimé par la carte. La recalculer dans le navigateur
   *  reviendrait à dupliquer le moteur statistique, et deux implémentations finissent
   *  toujours par diverger — sur le chiffre qui décide d'une livraison.
   *
   *  La fusion se fait borne par borne : saisir une limite supérieure ne doit pas effacer
   *  l'inférieure lue dans le fichier. */
  const changeTolerance = useCallback(
    (code: string, change: ToleranceInput) => {
      patch({
        tolerances: {
          ...overrides.tolerances,
          [code]: { ...overrides.tolerances?.[code], ...change },
        },
      });
    },
    [overrides.tolerances, patch],
  );

  /** Corriger la nature d'une série ne touche pas aux mesures : seul le diagnostic
   *  change de conclusion — l'autocorrélation d'un four est de la physique, celle d'une
   *  cote produit est une usure d'outil. On rejoue donc le fenêtrage, pas la lecture. */
  const changeNature = useCallback(
    (code: string, nature: string) => {
      const next = { ...natures, [code]: nature };
      setNatures(next);
      if (!result?.analysis_id) return;

      setBusy(true);
      changeWindowRemote(result.analysis_id, window, next)
        .then((fresh) => {
          setResult((previous) =>
            previous ? { ...previous, ...fresh } : previous,
          );
          setBusy(false);
        })
        .catch(() => setBusy(false));
    },
    [natures, result, window],
  );

  const rows = useMemo(
    () => (result ? buildRows(result.charts) : []),
    [result],
  );
  const chart = useMemo(
    () =>
      result?.charts.find((c) => c.characteristic_code === selected) ??
      result?.charts[0] ??
      null,
    [result, selected],
  );
  const corrections = Object.keys(overrides.roles ?? {}).length;

  return (
    <div className="space-y-3">
      {!result && !busy && (
        <div className="mb-4">
          <h1 className="text-xl font-semibold tracking-tight">
            Analyser un fichier machine
          </h1>
          <p className="mt-1 max-w-2xl text-[0.88rem] text-muted">
            Déposez l'export d'une machine que Capalys n'a jamais vue. La
            structure est détectée, les colonnes interprétées, les cartes de
            contrôle calculées — et tout ce que vous lirez restera modifiable.
          </p>
        </div>
      )}

      <DropZone onFile={onFile} busy={busy} compact={Boolean(result) || busy} />

      {error && (
        <Card className="border-bad/40 bg-bad-soft p-4">
          <div className="flex gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-bad" />
            <div>
              {/* Deux échecs très différents, et les confondre égare : l'analyse qui
                  refuse un fichier laisse l'écran vide, le dépôt qui refuse laisse
                  l'analyse intacte au-dessous. Le titre doit dire lequel des deux, sans
                  quoi l'utilisateur cherche un défaut dans son fichier alors que c'est
                  son nom de projet qui est en cause. */}
              <div className="text-[0.88rem] font-semibold text-bad">
                {result
                  ? "Ce dépôt n'a pas pu être enregistré."
                  : "Ce fichier n'a pas pu être analysé."}
              </div>
              <div className="mt-1 text-[0.82rem] text-muted">{error}</div>
            </div>
          </div>
        </Card>
      )}

      {busy && !result && <LoadingState />}

      {result && (
        <div
          className={cn(
            "space-y-3",
            busy && "pointer-events-none opacity-60 transition-opacity",
          )}
        >
          <Summary result={result} />

          {/* Où ce dépôt atterrit. Placé juste sous la synthèse et au-dessus des
              cartes : c'est la première question qu'un utilisateur se pose une fois
              qu'il a vu que le fichier a été compris. */}
          <ProjectPicker
            projects={projects}
            recognition={result.recognition}
            choice={choice}
            onChange={changeProject}
            onDeposit={deposer}
            deposited={deposit !== null}
            measurements={result.measurements_total}
            busy={busy}
            loading={loadingProjects}
          />

          {/* Ce que le dépôt a produit. Un chiffre, pas une couleur : « 1 240 mesures
              enregistrées » se vérifie dans l'onglet Projets, « enregistré ✓ » ne se
              vérifie nulle part. */}
          {deposit && (
            <Card
              className={cn(
                "px-4 py-2.5 text-[0.82rem]",
                deposit.already
                  ? "bg-surface-2 text-muted"
                  : "border-good/40 bg-good-soft text-good",
              )}
            >
              {deposit.already ? (
                <>
                  Ce contenu était déjà connu de « {deposit.project} » : aucune
                  mesure n'a été dupliquée. Les tolérances saisies, elles, ont
                  bien été enregistrées.
                </>
              ) : (
                <>
                  {num(deposit.measurements)} mesure(s) enregistrée(s) dans «{" "}
                  {deposit.project} ».
                  {deposit.rejected > 0
                    ? ` ${num(deposit.rejected)} ligne(s) écartée(s), conservées dans l'historique du dépôt.`
                    : ""}
                </>
              )}
            </Card>
          )}

          {result.project_required && (
            <Card className="border-warn/40 bg-warn-soft px-4 py-2.5 text-[0.82rem] text-warn">
              Ces mesures ne sont pas encore enregistrées : nommez le projet qui
              doit les recevoir. L'analyse ci-dessous est complète, rien n'est
              perdu.
            </Card>
          )}

          {result.format_changed && (
            <Card className="border-accent/40 bg-accent-soft px-4 py-2.5 text-[0.82rem] text-accent">
              Le format de ce projet a changé : le mapping passe en version{" "}
              {result.template_version} et la série continue sans coupure.
            </Card>
          )}

          {result.notes.map((n) => (
            <Card
              key={n}
              className="bg-accent-soft px-4 py-2.5 text-[0.82rem] text-accent"
            >
              {n}
            </Card>
          ))}

          {/* --- Vue d'ensemble : la réponse à « où est le problème ? » --- */}
          {rows.length > 0 && (
            <Card className="animate-[rise_0.35s_cubic-bezier(0.16,1,0.3,1)_both] overflow-hidden">
              <div className="border-b border-line bg-surface-2/60">
                <WindowPicker
                  window={window}
                  total={Math.max(
                    ...result.charts.map((c) => c.series_total),
                    1,
                  )}
                  hasTimestamps={result.columns.some((c) =>
                    ["timestamp", "date", "time"].includes(c.role),
                  )}
                  label={result.charts[0]?.window_label ?? ""}
                  onChange={changeWindow}
                />
              </div>
              <CardHeader
                title={
                  <span className="flex items-center gap-1.5">
                    <Table2 className="h-4 w-4 text-muted" />
                    Vue d'ensemble
                  </span>
                }
                hint="Trié par instabilité. Cliquer une ligne trace sa carte de contrôle."
                right={
                  <Badge tone="neutral">{rows.length} caractéristiques</Badge>
                }
              />
              <CharacteristicsTable
                rows={rows}
                selected={selected}
                onSelect={setSelected}
              />

              {Object.keys(result.charts_unavailable).length > 0 && (
                <details className="border-t border-line px-4 py-2.5">
                  <summary className="cursor-pointer text-[0.78rem] text-muted">
                    {Object.keys(result.charts_unavailable).length}{" "}
                    caractéristique(s) sans carte
                  </summary>
                  <ul className="mt-1.5 space-y-0.5 text-[0.76rem] text-faint">
                    {Object.entries(result.charts_unavailable).map(
                      ([code, why]) => (
                        <li key={code}>
                          <span className="font-mono text-muted">{code}</span> —{" "}
                          {why}
                        </li>
                      ),
                    )}
                  </ul>
                </details>
              )}
            </Card>
          )}

          {/* --- Carte de la caractéristique sélectionnée --- */}
          {chart && (
            <Card className="animate-[rise_0.35s_cubic-bezier(0.16,1,0.3,1)_both] overflow-hidden">
              <CardHeader
                title={
                  <span className="flex items-center gap-1.5">
                    <Layers className="h-4 w-4 text-muted" />
                    <span className="font-mono">
                      {chart.characteristic_code}
                    </span>
                  </span>
                }
                hint={
                  `Carte ${chart.chart_type} · ${num(chart.points_total)} points` +
                  (chart.points_total > chart.points.length
                    ? ` · série réduite à ${num(chart.points.length)} pour l'affichage`
                    : "") +
                  (chart.rules_evaluated_on < chart.points_total
                    ? ` · règles évaluées sur les ${num(chart.rules_evaluated_on)} dernières mesures`
                    : "")
                }
                right={
                  chart.violations_total > 0 ? (
                    <Badge tone="bad">
                      {num(chart.violations_total)} en violation
                    </Badge>
                  ) : (
                    <Badge tone="good">sous contrôle</Badge>
                  )
                }
              />
              <div className="px-2 pt-2">
                <PreviewChart chart={chart} />
              </div>
              {chart.diagnosis && (
                <DiagnosisPanel
                  diagnosis={chart.diagnosis}
                  code={chart.characteristic_code}
                  onNatureChange={changeNature}
                />
              )}
              {chart.violation_labels.length > 0 && (
                <ul className="flex flex-wrap gap-1.5 border-t border-line px-4 py-2.5">
                  {chart.violation_labels.map((l) => (
                    <li key={l}>
                      <Badge tone="bad">{l}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {chart && (
            <TolerancePanel
              code={chart.characteristic_code}
              tolerance={result.tolerances[chart.characteristic_code]}
              capability={chart.capability}
              onChange={changeTolerance}
              busy={busy}
            />
          )}

          <StructureSection result={result} onChange={patch} />
          <ColumnsSection
            columns={result.columns}
            uncertain={result.uncertain_columns}
            orientation={result.structure.orientation}
            mesures={result.characteristics_found}
            onChange={patch}
          />
          <MeasurementsSection result={result} />

          <p className="px-1 pt-1 text-[0.72rem] text-faint">
            Empreinte de format{" "}
            <span className="font-mono">{result.fingerprint}</span> — au
            prochain dépôt du même format, ce mapping sera appliqué sans rien
            demander.
            {corrections > 0 && (
              <span className="text-accent">
                {" "}
                · {corrections} correction(s) prises en compte.
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
